/**
 * isiXhosa: Settings
 *
 * What this file covers: Settings: profile, organisation, business hours, team and roles, notifications,
 * security, appearance and the other settings pages.
 *
 * Tone: Neutral and precise. Each setting says what changes when it is switched on.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const settings: Catalog = {
  'settings.nav.developer': 'Umphuhlisi',
  'settings.apiKeys.actions.create': 'Yenza isitshixo se-API',
  'settings.apiKeys.columns.prefix': 'Isimaphambili',
  'settings.apiKeys.empty': 'Akukho zitshixo ze-API okwangoku.',
  'settings.apiKeys.form.name': 'Igama lesitshixo',
  'settings.apiKeys.loadError': 'Ayikwazanga ukulayisha izitshixo ze-API.',
  'settings.apiKeys.modal.revoke.title': 'Rhoxisa isitshixo se-API?',
  'settings.apiKeys.toast.revoked': 'Isitshixo se-API sirhoxisiwe.',
  'settings.appearance.comfortable': 'Kukhululekile',
  'settings.appearance.compact': 'Kuxinene',
  'settings.appearance.cozy': 'Kufudumele',
  'settings.appearance.density': 'Uxinaniso',
  'settings.appearance.theme': 'Umxholo',
  'settings.badge.emailUnverified': 'I-imeyile ayiqinisekiswanga',
  'settings.badge.twoFactorOff': 'I-2FA ivaliwe',
  'settings.badge.twoFactorOn': 'I-2FA ivuliwe',
  'settings.cannedReplies.deleteFailed': 'Ayikwazanga ukucima.',
  'settings.cannedReplies.loadError': 'Ayikwazanga ukulayisha iimpendulo ezilungiselelweyo.',
  'settings.nav.apiKeys': 'Izitshixo ze-API',
  'settings.nav.appearance': 'Imbonakalo',
  'settings.nav.notifications': 'Izaziso',
  'settings.nav.organisation': 'Umbutho',
  'settings.nav.security': 'Ukhuseleko',
  'settings.nav.team': 'Iqela',
  'settings.nav.webhooks': 'Ii-webhook',
  'settings.notifications.actions.save': 'Gcina okukhethwayo',
  'settings.notifications.title': 'Izaziso nge-imeyile',
  'settings.notifications.toast.saved': 'Okukhethwayo kwezaziso kugciniwe.',
  'settings.organisation.timezone': 'Umda wexesha',
  'settings.organisation.toast.saved': 'Umbutho uhlaziyiwe.',
  'settings.profile.toast.saved': 'Iprofayile ihlaziyiwe.',
  'settings.security.actions.signOutAll': 'Phuma yonke indawo',
  'settings.security.activeSessions': 'Iiseshoni ezisebenzayo',
  'settings.security.changePassword': 'Tshintsha iphaswedi',
  'settings.security.confirmNewPassword': 'Qinisekisa iphaswedi entsha',
  'settings.security.currentPassword': 'Iphaswedi yangoku',
  'settings.security.modal.signOutAll.title': 'Phuma yonke indawo?',
  'settings.security.toast.changed': 'Iphaswedi itshintshiwe.',
  'settings.security.twoFactor.actions.disable': 'Vala i-2FA',
  'settings.security.twoFactor.actions.enable': 'Vumela i-2FA',
  'settings.security.twoFactor.actions.setup': 'Seta i-2FA',
  'settings.security.twoFactor.code': 'Ikhowudi',
  'settings.security.twoFactor.title': 'Uqinisekiso lwamanyathelo amabini',
  'settings.team.actions.invite': 'Mema ilungu leqela',
  'settings.team.columns.joined': 'Ijoyine',
  'settings.team.form.tempPassword': 'Iphaswedi yethutyana',
  'settings.team.inviteFailed': 'Ukumema kusilele',
  'settings.team.loadError': 'Ayikwazanga ukulayisha iqela.',
  'settings.webhooks.actions.add': 'Yongeza i-webhook',
  'settings.webhooks.columns.events': 'Iziganeko',
  'settings.webhooks.empty': 'Akukho kubhalisela ii-webhook okwangoku.',
  'settings.webhooks.form.events': 'Iintlobo zeziganeko',
  'settings.webhooks.form.url': 'I-URL yendawo yokugqibela',
  'settings.webhooks.loadError': 'Ayikwazanga ukulayisha ii-webhook.',
  'settings.webhooks.modal.delete.title': 'Cima i-webhook?',
  'settings.webhooks.title': 'Ukubhalisela ii-webhook',
  'settings.webhooks.toast.deleted': 'I-webhook icimiwe.',
  'settings.webhooks.deliveries.response': 'Oko kuthunyelwe umamkeli',
  'settings.webhooks.deliveries.showing': 'Kubonisa {{shown}} kwe-{{total}}',
  'settings.webhooks.result.success': 'Kuziswe',
  'settings.webhooks.result.blacklisted': 'Kwaliwe ngumamkeli',
  'settings.notifications.channel.email': 'I-imeyile',
  'settings.notifications.channel.whatsapp': 'I-WhatsApp',
  'settings.notifications.channel.webhook': 'I-webhook',
  'settings.notifications.channel.inApp': 'Kwi-app',
  'settings.notifications.toggle.depleted.label': 'Ibhalansi iphelile',
  'settings.notifications.toggle.recovered.label': 'Ibhalansi ibuyile',
  'settings.notifications.toggle.dailySpend.label': 'Inkcitho yemihla ngemihla',
  'settings.notifications.toggle.autoTopupSucceeded.label': 'Ukongezwa okuzenzekelayo kuphumelele',
  'settings.notifications.toggle.autoTopupFailed.label': 'Ukongezwa okuzenzekelayo kusilele',
  'settings.apiKeys.seeDocs': 'Funda amaxwebhu e-API apheleleyo',
  'settings.nav.contactFields': 'Iindawo zoqhagamshelwano',
  'settings.hub.contactFields.hint': 'Iinkcukacha ezongezelelekileyo ozigcinayo ngomntu ngamnye oqhagamshelana naye.',
  'settings.assistant.smallTalk.title': 'Iincoko ezincinci',
  'settings.assistant.smallTalk.intro': 'Iimpendulo kwimiyalezo engumbulelo kuphela, ukuvuma okanye ukuvalelisa, njengo-"enkosi", "k" okanye "usale kakuhle". Zithunyelwa kwangoko, azihlawulelwa kwaye azize zikhokelele kwindlela yokungaqiniseki. Ulwimi lokuncokola namagama aseMzantsi Afrika (tnx, sharp, dankie, ngiyabonga) ayaqondakala.',
  'settings.assistant.smallTalk.enabled': 'Phendula iincoko ezincinci',
  'settings.assistant.smallTalk.thanks': 'Xa umthengi ebulela',
  'settings.assistant.smallTalk.acknowledge': 'Xa umthengi esithi ok',
  'settings.assistant.smallTalk.goodbye': 'Xa umthengi evalelisa',
  'settings.assistant.smallTalk.hint': '{{contact.name ?? there}} iba ligama lomthengi. Yishiye ingenanto ukuze kusetyenziswe okungagqibekanga.',
  'settings.assistant.moderation.title': 'Ulwimi olungamkelekanga',
  'settings.assistant.moderation.intro': 'Iintlambo, izithuko namagama ocalulo avela kubathengi afumana impendulo eqinileyo, kwaye incoko ifakwa ithegi nenqaku leqela lakho. I-ejenti inokuvala umthengi kwiiSetingi zencoko.',
  'settings.assistant.moderation.enabled': 'Beka iliso kulwimi olungamkelekanga',
  'settings.assistant.moderation.action': 'Emva kokuphendula',
  'settings.assistant.moderation.actionHint': 'Ukudlulisela kunqumamisa i-bot, ukuze iqela lakho liqhubeke nencoko.',
  'settings.assistant.moderation.actions.warn': 'Qhubeka uphendula',
  'settings.assistant.moderation.actions.handover': 'Dlulisela incoko kwiqela',
  'settings.assistant.moderation.reply': 'Impendulo kumthengi',
  'settings.assistant.moderation.replyHint': 'Yishiye ingenanto ukuze kusetyenziswe okungagqibekanga.',
  'settings.assistant.moderation.extra': 'Amagama okongeza',
  'settings.assistant.moderation.extraHint': 'Igama elinye okanye ibinzana kumgca ngamnye. Kuthelekiswa amagama apheleleyo, kuquka nopelo olufihliweyo olufana ne-f*ck.',
  'settings.assistant.moderation.allowed': 'Amagama okuvumela',
  'settings.assistant.moderation.allowedHint': 'Amagama akuluhlu olungagqibekanga ishishini lakho elingakhathazwa ngawo, elinye kumgca ngamnye.',
  'settings.assistant.moderation.showDefault': 'Jonga uluhlu olungagqibekanga (amagama angama-{{count}})',
  'settings.assistant.moderation.hideDefault': 'Fihla uluhlu olungagqibekanga',
  'settings.assistant.pauseNotice.title': 'Xa iqela lakho linencoko',
  'settings.assistant.pauseNotice.intro': 'Umthengi obhala ngoxa umntu enencoko uxelelwa kanye ngosuku, ngamaqhosha okulinda iqela okanye ukubuza umncedisi. Akukho nto ithunyelwayo ukuba iqela lakho liphendule kwimizuzu eli-10 edlulileyo.',
  'settings.assistant.pauseNotice.enabled': 'Xelela umthengi',
  'settings.assistant.pauseNotice.message': 'Umyalezo',
  'settings.assistant.pauseNotice.hint': 'Yishiye ingenanto ukuze kusetyenziswe okungagqibekanga.'
};

export default settings;
